import { Controller } from "@hotwired/stimulus"

// Sample résumé used by the marketing demo ONLY. It is never the user's own
// document — the on-screen translation result is plain text, so a
// layout-preserved preview of a real upload would sell a capability the page
// does not render. See the redesign spec, §6.
const NAME = "Ana Rivera"
const LANGS = [
  { c: "es", n: "Spanish",  ro: "Ingeniera de Software Senior",
    b: ["Lideró la migración de la plataforma a Kubernetes.", "Redujo el gasto en la nube un 15%.", "Mentoró a cuatro ingenieros junior."],
    ch: ["Tu resumen y descripciones de puesto", "Viñetas y logros", "Habilidades y etiquetas de sección", "Todo lo que lee un reclutador"] },
  { c: "de", n: "German",   ro: "Senior-Softwareentwicklerin",
    b: ["Leitete die Migration der Plattform zu Kubernetes.", "Senkte die Cloud-Kosten um 15%.", "Betreute vier Nachwuchsentwickler."],
    ch: ["Zusammenfassung und Rollenbeschreibungen", "Stichpunkte und Erfolge", "Fähigkeiten und Abschnittstitel", "Alles, was ein Recruiter liest"] },
  { c: "fr", n: "French",   ro: "Ingénieure Logiciel Senior",
    b: ["A dirigé la migration de la plateforme vers Kubernetes.", "A réduit les coûts cloud de 15%.", "A encadré quatre ingénieurs juniors."],
    ch: ["Votre résumé et descriptions de poste", "Puces et réalisations", "Compétences et intitulés de section", "Tout ce que lit un recruteur"] },
  { c: "zh", n: "Chinese",  s: "airt-s-zh", ro: "高级软件工程师",
    b: ["主导平台向 Kubernetes 的迁移。", "将云支出降低了 15%。", "指导了四名初级工程师。"],
    ch: ["您的个人简介与职位描述", "要点与成就", "技能与栏目标题", "招聘人员阅读的一切"] },
  { c: "ja", n: "Japanese", s: "airt-s-ja", ro: "シニアソフトウェアエンジニア",
    b: ["プラットフォームの Kubernetes 移行を主導。", "クラウド費用を 15% 削減。", "4 名の若手エンジニアを指導。"],
    ch: ["要約と職務内容", "箇条書きと実績", "スキルとセクション見出し", "採用担当者が読むすべて"] },
  { c: "hi", n: "Hindi",    s: "airt-s-hi", ro: "वरिष्ठ सॉफ़्टवेयर इंजीनियर",
    b: ["प्लेटफ़ॉर्म को Kubernetes पर माइग्रेट करने का नेतृत्व किया।", "क्लाउड खर्च में 15% की कमी की।", "चार जूनियर इंजीनियरों का मार्गदर्शन किया।"],
    ch: ["आपका सारांश और भूमिका विवरण", "बुलेट पॉइंट और उपलब्धियाँ", "कौशल और अनुभाग शीर्षक", "वह सब जो भर्तीकर्ता पढ़ता है"] },
  { c: "ar", n: "Arabic",   s: "airt-s-ar", rtl: true, ro: "مهندسة برمجيات أولى",
    b: ["قادت ترحيل المنصة إلى Kubernetes.", "خفّضت إنفاق السحابة بنسبة 15%.", "أرشدت أربعة مهندسين مبتدئين."],
    ch: ["ملخصك وأوصاف الأدوار", "النقاط والإنجازات", "المهارات وعناوين الأقسام", "كل ما يقرأه المُوظِّف"] },
  { c: "en", n: "English",  ro: "Senior Software Engineer",
    b: ["Led the platform migration to Kubernetes.", "Cut cloud spend by 15%.", "Mentored four junior engineers."],
    ch: ["Your summary and role descriptions", "Bullet points and achievements", "Skills and section labels", "Everything a recruiter reads"] }
]

export default class extends Controller {
  static targets = ["doc", "stamp", "stage", "pill", "changes", "langLabel", "panelRole", "panelBullet"]
  static values  = { intervalMs: { type: Number, default: 2600 } }

  connect() {
    this.i = 0
    this.swapTimer = null
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.tick()
    if (!this.reduced) this.timer = setInterval(() => this.tick(), this.intervalMsValue)
  }

  // Turbo caches and restores pages; without this the interval survives the
  // visit and a second one is started on the next connect(), compounding.
  disconnect() {
    clearInterval(this.timer)
    clearTimeout(this.swapTimer)
    this.timer = null
  }

  tick() {
    this.render(LANGS[this.i])
    this.i = (this.i + 1) % LANGS.length
  }

  // Clicking a pill takes manual control and stops the loop.
  pick(event) {
    const code = event.currentTarget.dataset.lang
    clearInterval(this.timer)
    this.timer = null
    this.render(LANGS.find((l) => l.c === code))
  }

  render(l) {
    if (!l) return
    this.renderDoc(l)
    this.renderChanges(l)
    this.renderPanel(l)
    // `is-on` and `aria-pressed` are written in ONE statement so they can never
    // disagree — the state has to follow the 2.6s clock, not just a click, and
    // `is-on` alone is invisible to assistive tech.
    this.pillTargets.forEach((p) => {
      const on = p.dataset.lang === l.c
      p.classList.toggle("is-on", on)
      p.setAttribute("aria-pressed", String(on))
    })
  }

  // Every region this controller rewrites is `aria-hidden` with a static
  // English twin beside it, so this is NOT what makes the demo accessible.
  // It is what keeps the markup honest for everything that reads lang/dir
  // regardless of the accessibility tree: per-script font fallback, the bidi
  // base direction of the Arabic run (CSS-only until now — delete
  // `.airt-s-ar`'s `direction` and the attribute still holds it), find-in-page
  // and browser translation. One helper, so both surfaces get it from one place
  // — the apex and dashboard heroes share every target name.
  // Attribute-only: renderDoc/renderChanges/renderPanel reassign `className`,
  // which does not touch attributes, so a tick cannot strip these.
  mark(el, l) {
    if (!el) return
    el.setAttribute("lang", l.c)
    el.setAttribute("dir", l.rtl ? "rtl" : "ltr")
  }

  renderDoc(l) {
    if (!this.hasDocTarget) return
    this.docTarget.className = `airt-doc ${l.s || ""}`
    this.mark(this.docTarget, l)
    if (this.hasStageTarget) this.stageTarget.classList.toggle("is-rtl", !!l.rtl)
    this.docTarget.replaceChildren(
      this.el("div", "airt-doc-nm", NAME),
      this.el("div", "airt-doc-ro", l.ro),
      this.list(l.b)
    )
    if (this.hasStampTarget) this.stampTarget.textContent = l.n
  }

  renderChanges(l) {
    if (!this.hasChangesTarget) return
    this.changesTarget.className = `airt-exlist ${l.s || ""}`
    this.mark(this.changesTarget, l)
    this.changesTarget.replaceChildren(...l.ch.map((t) => this.el("li", "", t)))
    if (this.hasLangLabelTarget) this.langLabelTarget.textContent = l.n
  }

  // The four rules in this panel are pinned in CSS and never move; only the
  // words between them change. Direction and text MUST swap in the same frame:
  // setting the class up front and the text on a timer renders the OUTGOING
  // language in the INCOMING direction (Japanese laid out right-to-left).
  // Clearing the pending timer stops fast clicks resolving out of order.
  renderPanel(l) {
    if (!this.hasPanelRoleTarget) return
    const fields = [this.panelRoleTarget, ...this.panelBulletTargets]
    const values = [l.ro, ...l.b]
    fields.forEach((f) => { f.style.opacity = 0 })
    clearTimeout(this.swapTimer)
    const apply = () => {
      const mdoc = this.panelRoleTarget.closest(".airt-mdoc")
      mdoc.className = `airt-mdoc ${l.s || ""}`
      this.mark(mdoc, l)
      fields.forEach((f, n) => { f.textContent = values[n]; f.style.opacity = 1 })
    }
    if (this.reduced) { apply(); return }
    this.swapTimer = setTimeout(apply, 140)
  }

  el(tag, cls, text) {
    const n = document.createElement(tag)
    if (cls) n.className = cls
    n.textContent = text
    return n
  }

  list(items) {
    const ul = this.el("div", "airt-doc-bl", "")
    ul.replaceChildren(...items.map((t) => this.el("div", "", t)))
    return ul
  }
}
